import { BaseService } from '../base';

/** Uma linha de prêmio emitido (grid de Gestão de Carteira → Prêmios Emitidos). */
export interface IssuedPremium {
  advisoryName: string | null;
  advisoryTaxId: string | null;
  brokerName: string | null;
  brokerTaxId: string | null;
  productName: string | null;
  clientName: string | null;
  clientTaxId: string | null;
  policyCode: number | null;
  subNumber: string | null;
  endorsementNumber: number | null;
  coverageStartDate: string | null;
  coverageEndDate: string | null;
  issueDate: string | null;
  totalAmount: number | null;
  paidAmount: number | null;
  pendingAmount: number | null;
  statusCode: number | null;
  statusName: string | null;
}

export interface IssuedPremiumsSummary {
  emissions: number;
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
}

export interface IssuedPremiumsResponse {
  data: IssuedPremium[];
  meta: {
    pageNumber: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface GetIssuedPremiumsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortType?: 'asc' | 'desc';
  /** Período de emissão (obrigatório, janela máx. 6 meses), YYYY-MM-DD. */
  issueDateFrom?: string;
  issueDateTo?: string;
  /** PartyCode da assessoria (só seguradora). */
  advisoryPartyCode?: string;
  /** PartyCode do corretor (seguradora e assessoria). */
  brokerPartyCode?: string;
  productCode?: string;
  branchCode?: number;
  statusCode?: number;
  /** Busca livre por apólice, CNPJ/CPF ou nome do cliente. */
  search?: string;
}

/** Opção de dropdown dos filtros da Gestão de Carteira. */
export interface ReportFilterOption {
  value: string;
  label: string;
}

/** Opções servidas pelo backend para os filtros (Prêmios e Parcelas). */
export interface ReportFilterOptions {
  products: ReportFilterOption[];
  branches: ReportFilterOption[];
  policyStatuses: ReportFilterOption[];
  installmentStatuses: ReportFilterOption[];
  periodTypes: ReportFilterOption[];
}

/** Uma linha do Extrato de Parcelas. */
export interface Installment {
  advisoryName: string | null;
  advisoryTaxId: string | null;
  brokerName: string | null;
  brokerTaxId: string | null;
  /**
   * Corretora da linha ativa? `null` = sem CNPJ ou status indisponível (a Api
   * decide no download do boleto).
   */
  brokerActive?: boolean | null;
  productName: string | null;
  clientName: string | null;
  clientTaxId: string | null;
  policyCode: number | null;
  subNumber: string | null;
  stipulatorName: string | null;
  endorsementId: number | null;
  endorsementNumber: number | null;
  coverageStartDate: string | null;
  coverageEndDate: string | null;
  issueDate: string | null;
  installmentId: number | null;
  installmentNumber: number | null;
  dueDate: string | null;
  amount: number | null;
  paymentMethod: string | null;
  /** Status derivado da parcela: 1 Cancelada / 2 Inadimplente / 3 A vencer / 4 Paga. */
  statusCode: number | null;
  /** Rótulo derivado: Cancelada / Inadimplente / A vencer / Paga. */
  statusName: string | null;
}

/** Um bucket dos cards do Extrato (contagem + valor). */
export interface InstallmentBucket {
  count: number;
  amount: number;
}

/** Cards do Extrato de Parcelas. */
export interface InstallmentsSummary {
  generated: InstallmentBucket;
  paid: InstallmentBucket;
  overdue: InstallmentBucket;
  toExpire: InstallmentBucket;
  cancelled: InstallmentBucket;
}

export interface InstallmentsResponse {
  data: Installment[];
  meta: {
    pageNumber: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface GetInstallmentsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortType?: 'asc' | 'desc';
  /** Período (obrigatório, janela máx. 3 meses), YYYY-MM-DD. */
  periodFrom?: string;
  periodTo?: string;
  /** Tipo de período: 1 = Emissão, 2 = Vencimento. */
  periodType?: number;
  advisoryPartyCode?: string;
  brokerPartyCode?: string;
  productCode?: string;
  branchCode?: number;
  /** Status derivado da parcela: '1' Cancelada / '2' Inadimplente / '3' A vencer / '4' Paga. */
  installmentStatus?: string;
  /** Busca livre por apólice, CNPJ/CPF ou nome do cliente. */
  search?: string;
}

function buildInstallmentsQuery(
  params: GetInstallmentsParams
): URLSearchParams {
  const q = new URLSearchParams();
  if (params.page) q.append('pageNumber', String(params.page));
  if (params.pageSize) q.append('pageSize', String(params.pageSize));
  if (params.sortField) q.append('sortField', params.sortField);
  if (params.sortType) q.append('sortType', params.sortType);
  if (params.periodFrom) q.append('periodFrom', params.periodFrom);
  if (params.periodTo) q.append('periodTo', params.periodTo);
  if (params.periodType != null)
    q.append('periodType', String(params.periodType));
  if (params.advisoryPartyCode)
    q.append('advisoryPartyCode', params.advisoryPartyCode);
  if (params.brokerPartyCode)
    q.append('brokerPartyCode', params.brokerPartyCode);
  if (params.productCode) q.append('productCode', params.productCode);
  if (params.branchCode != null)
    q.append('branchCode', String(params.branchCode));
  if (params.installmentStatus)
    q.append('installmentStatus', params.installmentStatus);
  if (params.search) q.append('search', params.search);
  return q;
}

function buildQuery(params: GetIssuedPremiumsParams): URLSearchParams {
  const q = new URLSearchParams();
  if (params.page) q.append('pageNumber', String(params.page));
  if (params.pageSize) q.append('pageSize', String(params.pageSize));
  if (params.sortField) q.append('sortField', params.sortField);
  if (params.sortType) q.append('sortType', params.sortType);
  if (params.issueDateFrom) q.append('issueDateFrom', params.issueDateFrom);
  if (params.issueDateTo) q.append('issueDateTo', params.issueDateTo);
  if (params.advisoryPartyCode)
    q.append('advisoryPartyCode', params.advisoryPartyCode);
  if (params.brokerPartyCode)
    q.append('brokerPartyCode', params.brokerPartyCode);
  if (params.productCode) q.append('productCode', params.productCode);
  if (params.branchCode != null)
    q.append('branchCode', String(params.branchCode));
  if (params.statusCode != null)
    q.append('statusCode', String(params.statusCode));
  if (params.search) q.append('search', params.search);
  return q;
}

/** Uma linha crua de "Meus Recibos" (Gestão Financeira) vinda do Portal.Api. */
export interface ReceiptApiRow {
  beneficiaryPersonId: number | null;
  beneficiaryName: string | null;
  beneficiaryTaxId: string | null;
  receiptId: number | null;
  receiptNumber: number | null;
  netAmount: number | null;
  paymentForecast: string | null;
  paymentDate: string | null;
  /** 'Pago' / 'Pendente'. */
  status: string | null;
  /** 1 = Assessoria, 2 = Corretor. */
  beneficiaryType: number | null;
  paymentGroupName: string | null;
}

export interface ReceiptsApiResponse {
  data: ReceiptApiRow[];
  meta: {
    pageNumber: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface ReceiptsApiBucket {
  count: number;
  amount: number;
}

/** Para o Corretor a API devolve só `broker` (sem `total` e `advisory`). */
export interface ReceiptsApiSummary {
  total?: ReceiptsApiBucket;
  advisory?: ReceiptsApiBucket;
  broker: ReceiptsApiBucket;
}

export interface GetReceiptsApiParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortType?: 'asc' | 'desc';
  /** Meses retroativos (slider "Meses"). */
  months?: number;
  /** Range de datas (override do slider), YYYY-MM-DD. */
  dateFrom?: string;
  dateTo?: string;
  /** Tipo de favorecido (só Seguradora): 1 = Assessoria, 2 = Corretor. */
  beneficiaryType?: number;
  advisoryPartyCode?: string;
  brokerPartyCode?: string;
  /** Status do recibo (cd_status): 132 = Pago. */
  statusCode?: number;
  search?: string;
}

function buildReceiptsQuery(params: GetReceiptsApiParams): URLSearchParams {
  const q = new URLSearchParams();
  if (params.page) q.append('pageNumber', String(params.page));
  if (params.pageSize) q.append('pageSize', String(params.pageSize));
  if (params.sortField) q.append('sortField', params.sortField);
  if (params.sortType) q.append('sortType', params.sortType);
  if (params.months != null) q.append('months', String(params.months));
  if (params.dateFrom) q.append('dateFrom', params.dateFrom);
  if (params.dateTo) q.append('dateTo', params.dateTo);
  if (params.beneficiaryType != null)
    q.append('beneficiaryType', String(params.beneficiaryType));
  if (params.advisoryPartyCode)
    q.append('advisoryPartyCode', params.advisoryPartyCode);
  if (params.brokerPartyCode)
    q.append('brokerPartyCode', params.brokerPartyCode);
  if (params.statusCode != null)
    q.append('statusCode', String(params.statusCode));
  if (params.search) q.append('search', params.search);
  return q;
}

export class ReportsService extends BaseService {
  private customBaseUrl = import.meta.env.VITE_API_URL;

  async listIssuedPremiums(params: GetIssuedPremiumsParams = {}): Promise<{
    success: boolean;
    data?: IssuedPremiumsResponse;
    error?: string;
  }> {
    try {
      const response = await this.get(
        `/portal/v1/reports/issued-premiums?${buildQuery(params).toString()}`,
        {},
        this.customBaseUrl
      );
      return await this.handleJsonResponse<IssuedPremiumsResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /**
   * Cards totalizadores — endpoint separado da listagem. Recebe só os filtros
   * (sem paginação/ordenação), então é cacheável por conjunto de filtros.
   */
  async getIssuedPremiumsSummary(
    params: GetIssuedPremiumsParams = {}
  ): Promise<{
    success: boolean;
    data?: IssuedPremiumsSummary;
    error?: string;
  }> {
    try {
      const filtersOnly: GetIssuedPremiumsParams = {
        issueDateFrom: params.issueDateFrom,
        issueDateTo: params.issueDateTo,
        advisoryPartyCode: params.advisoryPartyCode,
        brokerPartyCode: params.brokerPartyCode,
        productCode: params.productCode,
        branchCode: params.branchCode,
        statusCode: params.statusCode,
        search: params.search,
      };
      const response = await this.get(
        `/portal/v1/reports/issued-premiums/summary?${buildQuery(filtersOnly).toString()}`,
        {},
        this.customBaseUrl
      );
      return await this.handleJsonResponse<IssuedPremiumsSummary>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /** Baixa o .xlsx dos prêmios emitidos da busca atual (resposta bruta = blob). */
  async exportIssuedPremiums(
    params: GetIssuedPremiumsParams = {}
  ): Promise<Response> {
    return this.get(
      `/portal/v1/reports/issued-premiums/export?${buildQuery(params).toString()}`,
      {},
      this.customBaseUrl
    );
  }

  /** Opções dos filtros (produtos, filiais, status, tipos de período). */
  async getFilterOptions(): Promise<{
    success: boolean;
    data?: ReportFilterOptions;
    error?: string;
  }> {
    try {
      const response = await this.get(
        '/portal/v1/reports/filter-options',
        {},
        this.customBaseUrl
      );
      return await this.handleJsonResponse<ReportFilterOptions>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /** Extrato de Parcelas paginado. */
  async listInstallments(params: GetInstallmentsParams = {}): Promise<{
    success: boolean;
    data?: InstallmentsResponse;
    error?: string;
  }> {
    try {
      const response = await this.get(
        `/portal/v1/reports/installments?${buildInstallmentsQuery(params).toString()}`,
        {},
        this.customBaseUrl
      );
      return await this.handleJsonResponse<InstallmentsResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /** Cards do Extrato — endpoint separado, cacheável por conjunto de filtros. */
  async getInstallmentsSummary(params: GetInstallmentsParams = {}): Promise<{
    success: boolean;
    data?: InstallmentsSummary;
    error?: string;
  }> {
    try {
      // Os cards são o breakdown por status → NÃO enviam installmentStatus
      // (mostram todos os buckets mesmo com um status selecionado no grid).
      const filtersOnly: GetInstallmentsParams = {
        periodFrom: params.periodFrom,
        periodTo: params.periodTo,
        periodType: params.periodType,
        advisoryPartyCode: params.advisoryPartyCode,
        brokerPartyCode: params.brokerPartyCode,
        productCode: params.productCode,
        branchCode: params.branchCode,
        search: params.search,
      };
      const response = await this.get(
        `/portal/v1/reports/installments/summary?${buildInstallmentsQuery(filtersOnly).toString()}`,
        {},
        this.customBaseUrl
      );
      return await this.handleJsonResponse<InstallmentsSummary>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /** Baixa o .xlsx do Extrato de Parcelas da busca atual (resposta bruta = blob). */
  async exportInstallments(
    params: GetInstallmentsParams = {}
  ): Promise<Response> {
    return this.get(
      `/portal/v1/reports/installments/export?${buildInstallmentsQuery(params).toString()}`,
      {},
      this.customBaseUrl
    );
  }

  /**
   * 2ª via do boleto de uma parcela (PDF em base64), pelos ids nativos do i4pro.
   * `brokerTaxId` (CNPJ da corretora da linha) deixa a Api barrar corretora
   * inativa (403 → toast central via `handleJsonResponse`).
   */
  async getInstallmentBoleto(
    endorsementId: number,
    installmentId: number,
    brokerTaxId?: string | null
  ): Promise<{
    success: boolean;
    data?: { pdfBase64: string };
    error?: string;
  }> {
    try {
      const q = new URLSearchParams({
        endorsementId: String(endorsementId),
        installmentId: String(installmentId),
      });
      if (brokerTaxId) q.set('brokerTaxId', brokerTaxId);
      const response = await this.get(
        `/portal/v1/reports/installments/boleto?${q.toString()}`,
        {},
        this.customBaseUrl
      );
      return await this.handleJsonResponse<{ pdfBase64: string }>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /** Uma linha de "Meus Recibos" (Gestão Financeira). */
  async listReceipts(params: GetReceiptsApiParams = {}): Promise<{
    success: boolean;
    data?: ReceiptsApiResponse;
    error?: string;
  }> {
    try {
      const response = await this.get(
        `/portal/v1/reports/receipts?${buildReceiptsQuery(params).toString()}`,
        {},
        this.customBaseUrl
      );
      return await this.handleJsonResponse<ReceiptsApiResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /** Cards "Próximo Pagamento" (endpoint separado, cacheável por filtro). */
  async getReceiptsSummary(params: GetReceiptsApiParams = {}): Promise<{
    success: boolean;
    data?: ReceiptsApiSummary;
    error?: string;
  }> {
    try {
      const response = await this.get(
        `/portal/v1/reports/receipts/summary?${buildReceiptsQuery(params).toString()}`,
        {},
        this.customBaseUrl
      );
      return await this.handleJsonResponse<ReceiptsApiSummary>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /** Recibo em PDF (2ª via) — base64, pelo id do Lançamento (id_lancamento). */
  async getReceiptPdf(receiptId: number): Promise<{
    success: boolean;
    data?: { pdfBase64: string };
    error?: string;
  }> {
    try {
      const q = new URLSearchParams({ receiptId: String(receiptId) });
      const response = await this.get(
        `/portal/v1/reports/receipts/pdf?${q.toString()}`,
        {},
        this.customBaseUrl
      );
      return await this.handleJsonResponse<{ pdfBase64: string }>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /** Demonstrativo de Comissão em PDF (base64), por período + favorecido. */
  async printCommissionStatement(params: {
    beneficiaryType?: number;
    advisoryPartyCode?: string;
    brokerPartyCode?: string;
    dateFrom: string;
    dateTo: string;
  }): Promise<{
    success: boolean;
    data?: { pdfBase64: string };
    error?: string;
  }> {
    try {
      const q = new URLSearchParams();
      if (params.beneficiaryType != null)
        q.append('beneficiaryType', String(params.beneficiaryType));
      if (params.advisoryPartyCode)
        q.append('advisoryPartyCode', params.advisoryPartyCode);
      if (params.brokerPartyCode)
        q.append('brokerPartyCode', params.brokerPartyCode);
      q.append('dateFrom', params.dateFrom);
      q.append('dateTo', params.dateTo);
      const response = await this.get(
        `/portal/v1/reports/commission-statement/pdf?${q.toString()}`,
        {},
        this.customBaseUrl
      );
      return await this.handleJsonResponse<{ pdfBase64: string }>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }
}

export const reportsService = new ReportsService();
